import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxChip } from '@onyx/angular';
import { OnyxButtonComponent, OnyxChipsComponent } from '@onyx/angular';
import { ModalLoadingSectionComponent } from '../../../../../../common/components/modals/modal-loading-section/modal-loading-section.component';
import { ROUTE_CONTEXT } from '../../../../../../common/services/route.service';
import { TelematicsStore } from '../../../../../../common/stores/telematics.store';
import { SwapRouteVariant } from '../../../enums/swap/swap-route-variant';
import type { Swap } from '../../../interfaces/swap/swap';
import { SwapModalRouteItemComponent } from './swap-modal-route-item/swap-modal-route-item.component';

@Component({
  selector: 'app-swap-modal-route',
  imports: [
    SwapModalRouteItemComponent,
    OnyxChipsComponent,
    OnyxButtonComponent,
    ModalLoadingSectionComponent,
    TranslatePipe,
  ],
  providers: [{ provide: ROUTE_CONTEXT, useValue: 'swapPanel' }],
  templateUrl: './swap-modal-route.component.html',
  styleUrl: './swap-modal-route.component.scss',
})
export class SwapModalRouteComponent {
  protected readonly telematicsStore = inject(TelematicsStore);

  public readonly swap = input.required<Swap>();
  public readonly isDouble = input.required<boolean>();

  protected readonly I18N = 'map.swapPanel.swapModal';
  protected readonly CHIPS: OnyxChip<SwapRouteVariant>[] = [
    { name: `${this.I18N}.afterSwap`, value: SwapRouteVariant.CURRENT },
    { name: `${this.I18N}.beforeSwap`, value: SwapRouteVariant.INITIAL },
  ];

  protected mode = signal([SwapRouteVariant.CURRENT]);
  protected areRoutesExpanded = signal(false);

  protected isCurrent = computed(
    () => this.mode()[0] === SwapRouteVariant.CURRENT,
  );

  protected vehicleData = computed(() => {
    const { uuid, vehicle, otherVehicle } = this.swap();
    const telematicsItems = this.telematicsStore
      .telematicsItemsBySwapUuid()
      .get(uuid);

    const getFuelType = (vehicleUuid?: string) => {
      if (!vehicleUuid || !telematicsItems?.length) return null;

      const item = telematicsItems.find(({ uuid }) => uuid === vehicleUuid);
      return item?.fuelTankCapacity?.fuelType ?? null;
    };

    return {
      vehicleUuid: vehicle.uuid,
      otherVehicleUuid: otherVehicle?.uuid ?? null,
      vehicleFuelType: getFuelType(vehicle.uuid),
      otherVehicleFuelType: getFuelType(otherVehicle?.uuid),
    };
  });
  protected activeData = computed(() => {
    const swap = this.swap();
    const isCurrent = this.isCurrent();
    const primarySet = isCurrent ? swap.vehicleSet : swap.initialSet;
    const secondarySet = isCurrent
      ? swap.otherVehicleSet
      : swap.otherInitialSet;

    return {
      primary: {
        route: isCurrent ? swap.vehicleRoute : swap.initialVehicleRoute,
        trailer: primarySet?.trailer ?? null,
      },
      secondary: {
        route:
          (isCurrent
            ? swap.otherVehicleRoute
            : swap.initialOtherVehicleRoute) ?? [],
        trailer: secondarySet?.trailer ?? null,
      },
    };
  });

  protected toggleRoutesExpanded(): void {
    this.areRoutesExpanded.update((expanded) => !expanded);
  }
}
